import React from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut } from 'lucide-react';
import { useMarketplaceAuth } from './MarketplaceAuthContext';

const PUBLIC_URL = import.meta.env.VITE_PUBLIC_URL || '';

/**
 * Chrome for the marketplace surface. Thin white top nav over a warm off-white
 * (#f5f5f3) page. The BluePill logo links out to blue-pill.ai (same pattern as
 * the digital-twin chats); the in-app nav lives to the right, with a logout
 * action for signed-in users.
 */
const MarketplaceShell: React.FC<{ children: React.ReactNode; maxWidth?: string; bare?: boolean }> = ({
  children,
  maxWidth = 'max-w-[860px]',
  bare = false,
}) => {
  const navigate = useNavigate();
  const { isAuthenticated, logout } = useMarketplaceAuth();

  const onLogout = async () => {
    await logout();
    navigate('/marketplace');
  };

  return (
    <div className="min-h-screen bg-white text-[#111]">
      <header className="sticky top-0 z-10 border-b border-black/10 bg-white">
        <div className="flex items-center justify-between px-8 py-3">
          <a
            href="https://blue-pill.ai/"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center overflow-hidden transition-opacity hover:opacity-80"
            aria-label="BluePill"
          >
            {/* logo.png has large transparent top/bottom margins baked in; clip
                them with a negative vertical margin so the wordmark sits flush. */}
            <img src={`${PUBLIC_URL}/logo.png`} alt="BluePill" className="-my-4 h-20 w-auto object-contain" />
          </a>
          {/* In-app nav only for signed-in users; the gate/login/signup page
              (also wrapped in this shell) shows no Studies/Log out. There is no
              Dashboard — signed-in users are routed straight into their journey. */}
          {isAuthenticated && (
            <div className="flex items-center gap-4">
              <button onClick={() => navigate('/marketplace/history')} className="text-[13px] text-[#6b6b6b] hover:text-[#111]">
                Studies
              </button>
              <button
                onClick={onLogout}
                className="inline-flex items-center gap-1.5 text-[13px] text-[#6b6b6b] hover:text-[#111]"
              >
                <LogOut className="h-3.5 w-3.5" /> Log out
              </button>
            </div>
          )}
        </div>
      </header>
      {/* `bare` lets a self-contained, full-screen form (e.g. the concept test)
          own the whole canvas — no centered column, no padding. */}
      {bare ? (
        <main className="w-full">{children}</main>
      ) : (
        <main className={`mx-auto ${maxWidth} px-8 py-10`}>{children}</main>
      )}
    </div>
  );
};

export default MarketplaceShell;
